'use strict';

/**
 * UserRoleMembershipFilter — makes the Users list "Role" filter find every user who holds a selected role,
 * however that role is assigned. A role assignment can live in THREE disconnected places:
 *   - User.IDRole            — the base user level (Field Personnel / Office Staff / Office Manager / …)
 *   - UserRoleJoin.IDRole    — customer-level named-role grants (where e.g. "Lab Technician" lives)
 *   - UserContextRole.IDRole — project/lab-level role grants
 *
 * The filter UI is the stock external-join Role picker (declared on the User recordset's FieldFilterClauses,
 * marked `RoleMembershipUnion`). The stock filter resolution reaches only ONE join table, and the config-app
 * Meadow does NOT honor parenthesized OR groups (see AccessWorkspace-Provider note) — so a base-column OR an
 * external-join can't be expressed as one clause. Instead this patch intercepts the User record provider: when
 * the membership clause carries selected roles, it resolves the UNION of user ids across all three sources and
 * swaps the clause for a single `FBL~IDUser~INN~<union>` (which the FilterManager POSTs to `/Query`, so a large
 * membership rides the request body, not the URL). Resolution is cached per role-selection and pre-computed in
 * getRecords/getRecordSetCount (both async) before the synchronous clause swap in _prepareFilterState. If
 * resolution ever fails the clause is left untouched, degrading to the stock UserRoleJoin-only filter.
 */

const PAGE = 2000;
const MAX_MEMBERS = 50000;

/**
 * Patch the User recordset provider (idempotent) so the RoleMembershipUnion clause resolves the full union.
 * @param {any} pPict
 */
function patchUserRoleMembershipFilter(pPict, pAttempt)
{
	const tmpProvider = pPict.providers && pPict.providers['RSP-Provider-User'];
	if (!tmpProvider)
	{
		// The User recordset provider is created lazily (first list view). Retry briefly so the patch is in
		// place before the user opens the Users list; give up quietly after a few seconds.
		const tmpAttempt = Number(pAttempt) || 0;
		if (tmpAttempt < 40 && (typeof setTimeout === 'function'))
		{
			setTimeout(() => patchUserRoleMembershipFilter(pPict, tmpAttempt + 1), 500);
		}
		return;
	}
	if (tmpProvider._roleMembershipPatched) { return; }
	tmpProvider._roleMembershipPatched = true;
	tmpProvider._roleUnionCache = { Key: '__init__', Filter: '' };

	const fGetJSON = (pURL) =>
		new Promise((resolve) =>
		{
			const tmpRC = (pPict.fable && pPict.fable.HeadlightRestClient) || pPict.HeadlightRestClient;
			if (!tmpRC) { return resolve(null); }
			tmpRC.getJSON(pURL, (pError, pBody) => resolve(pError ? null : pBody));
		});

	// The membership clause currently in the User list's active filter state (marker-identified), if any.
	const fActiveClause = () =>
	{
		const tmpState = pPict.Bundle && pPict.Bundle._ActiveFilterState && pPict.Bundle._ActiveFilterState.User;
		const tmpClauses = (tmpState && Array.isArray(tmpState.FilterClauses)) ? tmpState.FilterClauses : [];
		return tmpClauses.find((pClause) => pClause && pClause.RoleMembershipUnion) || null;
	};
	const fRoleIDs = (pClause) => Array.from(new Set(((pClause && pClause.Values) || []).map(Number).filter((pID) => pID > 0))).sort((pA, pB) => pA - pB);

	// Distinct IDUsers from a join set (UserRoleJoin / UserContextRole) whose IDRole is one of the picks.
	const fJoinUsers = async (pSet, pRoleIDs, pInto) =>
	{
		const tmpFilter = `FBL~IDRole~INN~${pRoleIDs.join(',')}`;
		const tmpCount = await fGetJSON(`${pSet}/Count/FilteredTo/${tmpFilter}`);
		const tmpTotal = Math.min((tmpCount && (tmpCount.Count != null)) ? Number(tmpCount.Count) : MAX_MEMBERS, MAX_MEMBERS);
		for (let tmpStart = 0; tmpStart < tmpTotal; tmpStart += PAGE)
		{
			/* eslint-disable-next-line no-await-in-loop */
			const tmpRows = await fGetJSON(`${pSet}/FilteredTo/${tmpFilter}/${tmpStart}/${PAGE}`);
			if (!Array.isArray(tmpRows) || tmpRows.length === 0) { break; }
			tmpRows.forEach((pRow) => { const tmpID = Number(pRow.IDUser); if (tmpID > 0) { pInto.add(tmpID); } });
			if (tmpRows.length < PAGE) { break; }
		}
	};

	// Base-level users: User.IDRole is one of the picks, scoped to the session customer (non-deleted).
	const fBaseUsers = async (pRoleIDs, pInto) =>
	{
		const tmpUser = pPict.AppData && pPict.AppData.CurrentUser;
		const tmpCID = tmpUser ? Number(tmpUser.CustomerID || tmpUser.IDCustomer) : 0;
		const tmpFilter = `FBL~IDRole~INN~${pRoleIDs.join(',')}${tmpCID ? `~FBV~IDCustomer~EQ~${tmpCID}` : ''}~FBV~Deleted~EQ~0`;
		const tmpCount = await fGetJSON(`Users/Count/FilteredTo/${tmpFilter}`);
		const tmpTotal = Math.min((tmpCount && (tmpCount.Count != null)) ? Number(tmpCount.Count) : MAX_MEMBERS, MAX_MEMBERS);
		for (let tmpStart = 0; tmpStart < tmpTotal; tmpStart += PAGE)
		{
			/* eslint-disable-next-line no-await-in-loop */
			const tmpRows = await fGetJSON(`Users/FilteredTo/${tmpFilter}/${tmpStart}/${PAGE}`);
			if (!Array.isArray(tmpRows) || tmpRows.length === 0) { break; }
			tmpRows.forEach((pRow) => { const tmpID = Number(pRow.IDUser); if (tmpID > 0) { pInto.add(tmpID); } });
			if (tmpRows.length < PAGE) { break; }
		}
	};

	/** Resolve + cache the union RawFilter for the current role selection (no-op if unchanged). */
	tmpProvider._ensureRoleUnionAsync = async function ()
	{
		const tmpRoleIDs = fRoleIDs(fActiveClause());
		const tmpKey = tmpRoleIDs.join(',');
		if (this._roleUnionCache.Key === tmpKey) { return; }
		if (tmpRoleIDs.length === 0) { this._roleUnionCache = { Key: tmpKey, Filter: '' }; return; }
		try
		{
			const tmpUsers = new Set();
			await Promise.all(
				[
					fJoinUsers('UserRoleJoins', tmpRoleIDs, tmpUsers),
					fJoinUsers('UserContextRoles', tmpRoleIDs, tmpUsers),
					fBaseUsers(tmpRoleIDs, tmpUsers),
				]);
			const tmpIDs = Array.from(tmpUsers);
			// Empty union → a filter that matches nothing (IDUser can never be 0), so the list shows 0 rather than all.
			this._roleUnionCache = { Key: tmpKey, Filter: tmpIDs.length ? `FBL~IDUser~INN~${tmpIDs.join(',')}` : 'FBV~IDUser~EQ~0', Count: tmpIDs.length };
		}
		catch (pError)
		{
			// Degrade: Filter=null → the swap is skipped and the stock UserRoleJoin filter applies (still fixes the reported case).
			this._roleUnionCache = { Key: tmpKey, Filter: null };
			this.pict.log.warn(`UserRoleMembershipFilter: union resolution failed, using stock filter: ${pError && (pError.message || pError)}`);
		}
	};

	// Pre-resolve the union before the (synchronous) _prepareFilterState swap — for BOTH the records + count fetches.
	const fWrapAsync = (pName) =>
	{
		const tmpOriginal = tmpProvider[pName].bind(tmpProvider);
		tmpProvider[pName] = async function (pOptions)
		{
			if (fActiveClause()) { await this._ensureRoleUnionAsync(); }
			return tmpOriginal(pOptions);
		};
	};
	fWrapAsync('getRecords');
	fWrapAsync('getRecordSetCount');

	// Swap the membership clause for the resolved union RawFilter in the clause list handed to the FilterManager.
	// _prepareFilterState builds a fresh array (concat), so splicing it never mutates the global filter state.
	const tmpOrigPrepare = tmpProvider._prepareFilterState.bind(tmpProvider);
	tmpProvider._prepareFilterState = function (pEntity, pOptions, pResultAddress)
	{
		const tmpResult = tmpOrigPrepare(pEntity, pOptions, pResultAddress);
		try
		{
			const tmpClauses = tmpResult && tmpResult[0];
			if (!Array.isArray(tmpClauses)) { return tmpResult; }
			const tmpIdx = tmpClauses.findIndex((pClause) => pClause && pClause.RoleMembershipUnion);
			if (tmpIdx < 0) { return tmpResult; }
			const tmpKey = fRoleIDs(tmpClauses[tmpIdx]).join(',');
			const tmpCache = this._roleUnionCache;
			if (tmpCache && (tmpCache.Key === tmpKey) && (typeof tmpCache.Filter === 'string'))
			{
				if (tmpCache.Filter === '') { tmpClauses.splice(tmpIdx, 1); }               // no roles picked → no filtering
				else { tmpClauses.splice(tmpIdx, 1, { Type: 'RawFilter', Value: tmpCache.Filter }); }
			}
		}
		catch (pError) { /* leave the stock clause in place (degrade) */ }
		return tmpResult;
	};
}

module.exports = { patchUserRoleMembershipFilter };
